// Pure helpers for building WaveSurfer peak arrays. Kept free of DOM/audio
// dependencies so they can be unit tested.

export const PEAK_COUNT = 500;

/**
 * Convert the backend's `sampleData` field into normalized peaks (0..1).
 * The backend stores it as either an array or a bracketed string of 0..100
 * values, e.g. "[12, 48, 100, ...]". Returns null when unusable.
 */
export function parseSampleData(sampleData) {
  if (!sampleData) return null;

  let samples;
  if (typeof sampleData === "string") {
    samples = sampleData
      .replace(/^\s*\[|\]\s*$/g, "")
      .split(",")
      .map((s) => parseFloat(s));
  } else if (Array.isArray(sampleData)) {
    samples = sampleData.map(Number);
  } else {
    return null;
  }

  samples = samples.filter((n) => Number.isFinite(n)).map(Math.abs);
  if (samples.length === 0) return null;

  const max = Math.max(...samples);
  if (max <= 0) return null;

  const peaks = new Float32Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    peaks[i] = samples[i] / max;
  }
  return peaks;
}

/**
 * RMS energy per segment. Varies between quiet verses and loud choruses,
 * unlike raw peak amplitude which sits near max for most modern masters.
 */
export function computeRmsPeaks(audioBuffer, numPeaks = PEAK_COUNT) {
  const channel = audioBuffer.getChannelData(0);
  const samplesPerPeak = Math.max(1, Math.floor(channel.length / numPeaks));
  const peaks = new Float32Array(numPeaks);

  for (let i = 0; i < numPeaks; i++) {
    const start = i * samplesPerPeak;
    const end = Math.min(start + samplesPerPeak, channel.length);
    if (end <= start) break;
    let sum = 0;
    for (let j = start; j < end; j++) {
      sum += channel[j] * channel[j];
    }
    peaks[i] = Math.sqrt(sum / (end - start));
  }

  let maxPeak = 0;
  for (let i = 0; i < peaks.length; i++) {
    if (peaks[i] > maxPeak) maxPeak = peaks[i];
  }
  if (maxPeak > 0) {
    for (let i = 0; i < peaks.length; i++) {
      peaks[i] = peaks[i] / maxPeak;
    }
  }
  return peaks;
}

/** Neutral bars shown while no real peaks are available. */
export function placeholderPeaks(count = PEAK_COUNT) {
  const peaks = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    peaks[i] = 0.3 + Math.random() * 0.4;
  }
  return peaks;
}
